import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../Style/OurStory.css";
import NavBar from "../Components/NavBar.js";
import Footer from "../Components/Footer.js";
import storyCover from "../assets/OurStory/OurStoryCoverPhoto.png";
import balloonTransparent from "../assets/OurStory/HotAirBalloonTransparent.png";
import balloonTransparentSide from "../assets/OurStory/HotAirBalloonTransparentSide.png";
import weRiseBackground from "../assets/OurStory/WeRiseBackGround.png";

const FOUNDATION_ITEMS = [
  {
    number: "01",
    title: "Our Philosophy",
    paragraphs: [
      "We believe the spaces around us shape the way we feel.",
      "Light has the power to soften a room. Fragrance has the power to awaken memory. Beautiful objects have the power to slow us down.",
      "When thoughtfully brought together, they create more than atmosphere; they create moments worth returning to.",
    ],
  },
  {
    number: "02",
    title: "Our Mission",
    paragraphs: [
      "We create sculptural fragrance pieces that invite people to slow down, breathe deeply, and remember that even in life's heaviest seasons, beauty, light, and hope can still rise.",
    ],
  },
  {
    number: "03",
    title: "Brand Identity",
    paragraphs: [
      "Up & Away creates handcrafted fragrance sculptures inspired by elevation.",
      "Blending sculpture, light, fragrance, and floral artistry, each piece is designed to become a lasting presence within homes, hospitality spaces, celebrations, and the moments that matter most.",
      "Not simply to decorate a space, but to transform the feeling within it.",
    ],
  },
];

function OurStory() {
  const location = useLocation();
  const navigate = useNavigate();
  const storyRisePanelRef = useRef(null);

  useEffect(() => {
    if (!location.state?.scrollToStoryRisePanel) return;

    let scrollFrameId;
    const frameId = requestAnimationFrame(() => {
      scrollFrameId = requestAnimationFrame(() => {
        storyRisePanelRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });

        navigate(location.pathname, { replace: true, state: null });
      });
    });

    return () => {
      cancelAnimationFrame(frameId);
      cancelAnimationFrame(scrollFrameId);
    };
  }, [location.pathname, location.state, navigate]);

  return (
    <div className="our-story-page">
      <NavBar />

      <main>
        <section className="story-hero">
          <img
            className="story-hero-image"
            src={storyCover}
            alt="Floral hot air balloon sculpture floating above clouds"
          />
          <div className="story-hero-copy">
            <p>We were never meant</p>
            <p>to carry life without</p>
            <h1>moments</h1>
            <p>that lift us.</p>
          </div>
        </section>

        <section className="story-foundation">
          <img
            className="story-balloon story-balloon-top"
            src={balloonTransparent}
            alt=""
            aria-hidden="true"
          />
          <img
            className="story-balloon story-balloon-left"
            src={balloonTransparentSide}
            alt=""
            aria-hidden="true"
          />
          <img
            className="story-balloon story-balloon-right"
            src={balloonTransparentSide}
            alt=""
            aria-hidden="true"
          />
          <img
            className="story-balloon story-balloon-bottom"
            src={balloonTransparent}
            alt=""
            aria-hidden="true"
          />

          <header className="story-foundation-header">
            <p>The Foundation</p>
            <h2>
              Beauty, light, and hope
              <br />
              can still rise
            </h2>
            <span aria-hidden="true">
              <i />
              <b>⌁ ◊ ⌁</b>
              <i />
            </span>
          </header>

          <div className="story-foundation-timeline">
            {FOUNDATION_ITEMS.map((item) => (
              <article className="story-foundation-item" key={item.number}>
                {/* <p className="story-foundation-number">{item.number}</p> */}
                <span className="story-foundation-line" aria-hidden="true" />
                <h3>{item.title}</h3>
                {item.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </article>
            ))}
          </div>
        </section>

        <section className="story-rise" ref={storyRisePanelRef}>
          <img
            className="story-rise-bg"
            src={weRiseBackground}
            alt=""
            aria-hidden="true"
          />
          <div className="story-rise-panel">
            <p className="story-rise-kicker">Manifesto</p>
            <h2>We Rise.</h2>
            <p>We rise through uncertainty.</p>
            <p>We rise through change.</p>
            <p>
              We rise through the unseen battles that quietly shape who we
              become.
            </p>
            <p>
              Not because the weight disappears, but because hope becomes
              stronger than fear.
            </p>
            <p>Softness is strength. Beauty is healing.</p>
            <p>
              Every new day offers another chance to begin again.
              <br />
              This is our story. This is our inspiration.
            </p>
            <p>Inspired by Elevation.</p>
          </div>
        </section>

        <section className="story-empowerment">
          <p className="story-empowerment-kicker">Women Empowerment</p>
          <h2>
            I want them to
            <br />
            see themselves
            <br />
            in it.
          </h2>
          <span className="story-empowerment-rule" aria-hidden="true" />
          <div className="story-empowerment-copy">
            <p>
              I don't want women to buy one of my sculptures because it's
              beautiful. I want them to see themselves in it.
            </p>
            <p>
              To remember that strength isn't always loud. That softness isn't
              weakness. That healing doesn't always happen all at once. That
              carrying invisible battles doesn't make you fragile. It makes
              every step you continue to take an act of courage.
            </p>
            <p>
              <em>
                Like a balloon rising against gravity, we don't wait for life to
                become lighter before we rise. We rise anyway.
              </em>
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default OurStory;
